import { t } from "@lingui/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { AlertIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  currentSignUpRowAtom,
  eventSignUpProcessDispatchAtom,
  isGroupFullOfMembersAtom,
  isGroupFulOfGuestsAtom,
  memberHoldRegistrationsAtom,
  memberRegistrationsAtom,
  guestRegistrationsAtom,
  customerRegistrationsAtom,
  unknownGuestRegistrationsAtom,
  isGroupFullOfAttendeesAtom,
  isGroupFullOfCustomersAtom,
  notifyMembersAtom,
} from "../atoms/eventSignUpProcess";
import { AddPeopleRenderFunction, EventPlayer } from "../types";
import { AllowedGroupSizesWarning } from "./AllowedGroupSizesWarning";
import { FormStepHeader } from "./FormStepHeader";
import { GroupRegistrationDisplay } from "./GroupRegistrationDisplay/GroupRegistrationDisplay";
import { GroupRegistrationDisplay_event$key } from "./GroupRegistrationDisplay/__generated__/GroupRegistrationDisplay_event.graphql";

type AddPeopleStepProps = {
  addPeopleRenderFunction: AddPeopleRenderFunction;
  groupRegistrationDisplayFragmentRef: GroupRegistrationDisplay_event$key;
  spotsAvailable: number | null;
  allowedGroupSizes?: readonly (number | null | undefined)[];
};

export const AddPeopleStep = ({
  addPeopleRenderFunction,
  groupRegistrationDisplayFragmentRef,
  spotsAvailable,
  allowedGroupSizes,
}: AddPeopleStepProps) => {
  const currentSignUpRow = useAtomValue(currentSignUpRowAtom);
  const dispatchEventSignUpProcess = useSetAtom(eventSignUpProcessDispatchAtom);

  const isNewSignUp = !currentSignUpRow?.id;

  //TODO: fix
  const [searchString, setSearchString] = useState("");

  const [notifyMembers, setNotifyMembers] = useAtom(notifyMembersAtom);

  const isGroupFullOfMembersCheck = useAtomValue(isGroupFullOfMembersAtom);
  const isGroupFullOfGuestsCheck = useAtomValue(isGroupFulOfGuestsAtom);
  const isGroupFullOfCustomersCheck = useAtomValue(isGroupFullOfCustomersAtom);

  const isGroupFullOfAttendeesCheck = useAtomValue(
    isGroupFullOfAttendeesAtom(spotsAvailable)
  );

  const memberHoldRegistrations = useAtomValue(memberHoldRegistrationsAtom);
  const memberRegistrations = useAtomValue(memberRegistrationsAtom);
  const guestRegistrations = useAtomValue(guestRegistrationsAtom);
  const customerRegistrations = useAtomValue(customerRegistrationsAtom);
  const unknownGuestRegistrations = useAtomValue(unknownGuestRegistrationsAtom);

  //TODO: Probably also throw an error
  if (!currentSignUpRow) {
    return null;
  }

  const shouldShowMemberCapacityWarning =
    isGroupFullOfMembersCheck && memberRegistrations.length > 0;
  const shouldShowGuestCapacityWarning =
    isGroupFullOfGuestsCheck && guestRegistrations.length > 0;
  const shouldShowCustomerCapacityWarning =
    isGroupFullOfCustomersCheck && customerRegistrations.length > 0;
  const shouldShowCombinedMemberGuestCapacityWarning =
    shouldShowMemberCapacityWarning && shouldShowGuestCapacityWarning;

  const listHeaderComponent = (
    <>
      {allowedGroupSizes && allowedGroupSizes.length > 0 ? (
        <AllowedGroupSizesWarning allowedGroupSizes={allowedGroupSizes} />
      ) : null}
      {shouldShowCombinedMemberGuestCapacityWarning ? (
        <CapacityWarning text={t`Maximum capacity reached`} />
      ) : null}
      {shouldShowMemberCapacityWarning &&
      !shouldShowCombinedMemberGuestCapacityWarning ? (
        <CapacityWarning text={t`Maximum member capacity reached`} />
      ) : null}
      {shouldShowGuestCapacityWarning &&
      !shouldShowCombinedMemberGuestCapacityWarning ? (
        <CapacityWarning text={t`Maximum guest capacity reached`} />
      ) : null}
      {shouldShowCustomerCapacityWarning ? (
        <CapacityWarning text={t`Maximum customer capacity reached`} />
      ) : null}
    </>
  );

  return (
    <VStack flex={1}>
      <FormStepHeader
        title={t`Sign Up`}
        smallScreenLeftComponent={<Box width={5} />}
      />
      <GroupRegistrationDisplay
        fragmentRef={groupRegistrationDisplayFragmentRef}
        isNewSignUp={isNewSignUp}
      />
      <VStack flex={1}>
        {addPeopleRenderFunction({
          unknownGuestCount: unknownGuestRegistrations.length,
          memberHoldCount: memberHoldRegistrations.length,
          memberCount: memberRegistrations.length,
          guestCount: guestRegistrations.length,
          customerCount: customerRegistrations.length,
          currentRegistrations: currentSignUpRow.registrations,
          isGroupFullOfGuests: isGroupFullOfGuestsCheck,
          isGroupFullOfMembers: isGroupFullOfMembersCheck,
          isGroupFullOfAttendees: isGroupFullOfAttendeesCheck,
          isGroupFullOfCustomers: isGroupFullOfCustomersCheck,
          selectedPlayerIds: currentSignUpRow.registrations.flatMap(
            (registration) => {
              return registration.registrant.playerId &&
                !registration.registrant.playerId.includes("newguest")
                ? [registration.registrant.playerId]
                : [];
            }
          ),
          searchString: searchString,
          setSearchString: (val) => {
            setSearchString(val);
          },
          handleAddMemberClick: (player) => {
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: {
                player,
                type: "MEMBER",
              },
            });
          },
          handleAddGuestClick: (player) => {
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: {
                player,
                type: "GUEST",
              },
            });
          },
          handleNewGuestSubmission: (player) => {
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: {
                player: {
                  ...player,
                  playerId: `newguest:${uuidv4()}`,
                },
                type: "New Guest",
              },
            });
          },
          handleAddUnknownGuestClick: () => {
            const player: EventPlayer = {
              playerId: null,
            };
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: {
                player,
                type: "GUEST",
              },
            });
          },
          handleAddCustomerClick: (player) => {
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: { player, type: "CUSTOMER" },
            });
          },
          handleAddUnknownCustomerClick: () => {
            const player: EventPlayer = {
              playerId: null,
            };
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: { player, type: "CUSTOMER" },
            });
          },
          handleRemoveUnknownCustomerClick: () => {
            dispatchEventSignUpProcess({
              type: "removeUnknownCustomer",
            });
          },
          handleAddMemberHoldClick: () => {
            const player: EventPlayer = {
              playerId: null,
            };
            dispatchEventSignUpProcess({
              type: "addPlayer",
              payload: {
                player,
                type: "MEMBER",
              },
            });
          },
          handleRemoveMemberHoldClick: () => {
            dispatchEventSignUpProcess({
              type: "removeMemberHold",
            });
          },
          handleRemoveUnknownGuestClick: () => {
            dispatchEventSignUpProcess({
              type: "removeUnknownGuest",
            });
          },
          handleRemovePlayerClick: (playerId: string) => {
            const registration = currentSignUpRow.registrations.find(
              (r) => r.registrant.playerId === playerId
            );
            if (registration) {
              dispatchEventSignUpProcess({
                type: "removeRegistration",
                payload: { registration },
              });
            }
          },
          notifyMembers,
          setNotifyMembers,
          listHeaderComponent,
        })}
      </VStack>
    </VStack>
  );
};

export const CapacityWarning = ({ text }: { text: string }) => {
  return (
    <Box bg="surface-status-error-subtle" px={4} py={2} borderRadius="2">
      <HStack alignItems="center" gap={3}>
        <AlertIcon />
        <Text variant="mainTiny" color="text-error-inverse">
          {text}
        </Text>
      </HStack>
    </Box>
  );
};
